import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Segmented,
  Select,
  Space,
  Tabs,
  Typography,
  message,
} from 'antd'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { adminApi, formatApiError, getAdminDisplayName } from '../api'
import { HtmlDocEditor, type HtmlDocEditorHandle } from '../components/HtmlDocEditor'
import { MarkdownDocEditor, MarkdownDocViewer, type MarkdownDocEditorHandle } from '../components/MarkdownDocEditor'
import { DocTemplatePicker } from '../components/DocTemplatePicker'
import { DocSelectionAiToolbar } from '../components/DocSelectionAiToolbar'
import { DocEditorAtAiTrigger } from '../components/DocEditorAtAiTrigger'
import {
  DocInlineWritingPanel,
  type DocInlineInsertMode,
  type DocInlineWritingMode,
} from '../components/DocInlineWritingPanel'
import { DocWritingAssistant, type DocWritingAssistantHandle } from '../components/DocWritingAssistant'
import {
  KNOWLEDGE_SCOPE_META,
  type KnowledgeScope,
  scopeQuery,
} from '../constants/knowledgeScope'
import { useAdminAuth } from '../lib/useAdminAuth'
import { tagsFromForm, tagsToArray } from '../components/knowledgeTags'
import {
  fetchAttachmentBlob,
  findOnlineEditableFile,
  resolvePreviewKind,
  type FileRec,
} from '../components/knowledgeFilePreview'
import { type FolderLabel } from '../components/knowledgeFolderUtils'
import {
  KnowledgeCategoryBrowseField,
  KnowledgeDimensionMultiBrowseField,
  KnowledgeFolderMultiBrowseField,
  KnowledgeTagsBrowseField,
  PageLoading,
  formatDate,
  type FolderPlacement,
} from '../components/ui'
import { publishKnowledgeDocSaved } from '../services/knowledgeDocSync'
import { KnowledgeItemKeyValuesPreview } from '../components/KnowledgeItemKeyValuesPreview'
import { KnowledgeItemShareTab } from '../components/KnowledgeItemShareTab'
import { isDocumentBodyEmpty } from '../lib/documentBodyEmpty'
import './KnowledgeCreateDoc.css'

type DocType = 'markdown' | 'richtext'

type ChunkSettings = {
  default_strategy: string
  strategies: { id: string; label: string; enabled: boolean }[]
}

const DOC_TYPE_OPTIONS: { value: DocType; label: string }[] = [
  { value: 'richtext', label: '网页文档' },
  { value: 'markdown', label: 'Markdown' },
]

const DOC_STATUS_OPTIONS = [
  { value: 'normal', label: '正常' },
  { value: 'draft', label: '草稿' },
]

function parseDocType(raw: string | null): DocType {
  return raw === 'richtext' ? 'richtext' : 'markdown'
}

function scopeFromPath(pathname: string): KnowledgeScope {
  return pathname.startsWith('/enterprise') ? 'enterprise' : 'personal'
}

type SavedDocItem = {
  id: number
  title: string
  folder_id: number | null
  folder_dimension_id?: number | null
  dimension_ids?: number[]
  folder_ids?: number[]
  folder_placements?: FolderPlacement[]
  dimension_placements?: { dimension_id: number; dimension_name: string }[]
  category: string
  tags: string
  summary: string
  doc_status: string
  created_by_name: string
  created_at: string
  index_job_id?: string | null
}

type FolderDimension = {
  id: number
  name: string
  is_manual: boolean
}

function dimensionDisplayName(dim: FolderDimension): string {
  return dim.is_manual ? '系统目录' : dim.name
}

function defaultDimensionId(items: FolderDimension[]): number | undefined {
  if (!items.length) return undefined
  return (items.find((d) => d.is_manual) ?? items[0]).id
}

function docPath(scope: KnowledgeScope, folderId?: number | null, itemId?: number): string {
  const base = scope === 'enterprise' ? '/enterprise/create' : '/knowledge/create'
  const params = new URLSearchParams()
  if (folderId && folderId > 0) params.set('folder_id', String(folderId))
  if (itemId && itemId > 0) params.set('id', String(itemId))
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

function listPath(scope: KnowledgeScope): string {
  return scope === 'enterprise' ? '/enterprise' : '/knowledge'
}

function itemToSaved(item: SavedDocItem & { folder_id?: number | null }): SavedDocItem {
  return {
    id: item.id,
    title: item.title,
    folder_id: item.folder_id ?? null,
    folder_dimension_id: item.folder_dimension_id ?? null,
    dimension_ids: item.dimension_ids || [],
    folder_ids: item.folder_ids || (item.folder_id ? [item.folder_id] : []),
    folder_placements: item.folder_placements || [],
    dimension_placements: item.dimension_placements || [],
    category: item.category || '文档',
    tags: item.tags || '',
    summary: item.summary || '',
    doc_status: item.doc_status || 'normal',
    created_by_name: item.created_by_name || '',
    created_at: item.created_at,
  }
}

async function loadDocBody(
  itemId: number,
  files: FileRec[] | null | undefined,
  scopeParams: { scope: KnowledgeScope },
): Promise<{ docType: DocType; content: string }> {
  const rec = findOnlineEditableFile(files)
  if (!rec?.stored_name) {
    throw new Error('该文档为上传附件（Word/PDF 等），不支持在线编辑正文，请使用预览查看')
  }
  const kind = resolvePreviewKind(rec)
  const { blob } = await fetchAttachmentBlob(itemId, rec, scopeParams)
  const text = await blob.text()
  if (kind === 'markdown') {
    return { docType: 'markdown', content: text }
  }
  const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i)
  return { docType: 'richtext', content: bodyMatch ? bodyMatch[1] : text }
}

function StandaloneShell({ children, centered }: { children: ReactNode; centered?: boolean }) {
  return (
    <div className={`kb-create-doc-page${centered ? ' kb-create-doc-page--centered' : ''}`}>
      {children}
    </div>
  )
}

function parsePageMode(hasItemId: boolean, mode: string | null): 'create' | 'view' | 'edit' {
  if (!hasItemId) return 'create'
  return mode === 'edit' ? 'edit' : 'view'
}

export default function KnowledgeCreateDoc() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { ready, loggedIn } = useAdminAuth()

  const scope = scopeFromPath(location.pathname)
  const scopeParams = useMemo(() => scopeQuery(scope), [scope])
  const initialFolderId = Number(searchParams.get('folder_id') || '')
  const folderValid = Number.isFinite(initialFolderId) && initialFolderId > 0
  const initialDimensionId = Number(searchParams.get('dimension_id') || '')
  const viewItemId = Number(searchParams.get('id') || '')
  const viewItemIdValid = Number.isFinite(viewItemId) && viewItemId > 0

  const [form] = Form.useForm()
  const [pageMode, setPageMode] = useState<'create' | 'view' | 'edit'>(() =>
    parsePageMode(viewItemIdValid, searchParams.get('mode')),
  )
  const [savedItem, setSavedItem] = useState<SavedDocItem | null>(null)
  const [docLoading, setDocLoading] = useState(viewItemIdValid)
  const [docType, setDocType] = useState<DocType>(() => parseDocType(searchParams.get('type')))
  const [title, setTitle] = useState('')
  const [markdown, setMarkdown] = useState('')
  const [richtext, setRichtext] = useState('')
  const [editorBodyEmpty, setEditorBodyEmpty] = useState(true)
  const [submittingAs, setSubmittingAs] = useState<'normal' | 'draft' | null>(null)
  const [activeTab, setActiveTab] = useState('body')
  const mdEditorRef = useRef<MarkdownDocEditorHandle>(null)
  const htmlEditorRef = useRef<HtmlDocEditorHandle>(null)
  const editorMainRef = useRef<HTMLDivElement>(null)
  const writingAssistantRef = useRef<DocWritingAssistantHandle>(null)
  const writingPinnedSelectionRef = useRef('')
  const inlineSelectionRef = useRef({ selection: '', selectionPlain: '' })
  const [inlinePanelOpen, setInlinePanelOpen] = useState(false)
  const [inlinePanelMode, setInlinePanelMode] = useState<DocInlineWritingMode>('cursor')
  const [inlinePanelSelection, setInlinePanelSelection] = useState({ selection: '', selectionPlain: '' })
  const [defaultStrategy, setDefaultStrategy] = useState('fixed')
  const [dimensions, setDimensions] = useState<FolderDimension[]>([])
  const [folderMetaById, setFolderMetaById] = useState<Map<number, FolderLabel>>(new Map())
  const creatorName = getAdminDisplayName() || '当前用户'

  const folderIdsWatch = Form.useWatch('folder_ids', form) as number[] | undefined
  const dimensionIdsWatch = Form.useWatch('dimension_ids', form) as number[] | undefined
  const selectedFolderIdsKey = useMemo(
    () => (folderIdsWatch ?? []).join(','),
    [folderIdsWatch],
  )
  const selectedDimensionIds = dimensionIdsWatch ?? []
  const docStatusWatch = Form.useWatch('doc_status', form) as string | undefined
  const categoryWatch = Form.useWatch('category', form) as string | undefined
  const selectedFolder = useMemo(() => {
    for (const fid of folderIdsWatch ?? []) {
      const meta = folderMetaById.get(fid)
      if (meta) return meta
    }
    return null
  }, [folderMetaById, folderIdsWatch])
  const shareFolderId = useMemo(() => {
    const ids = folderIdsWatch ?? []
    if (ids.length) return ids[0]
    if (folderValid) return initialFolderId
    return savedItem?.folder_id ?? null
  }, [folderIdsWatch, folderValid, initialFolderId, savedItem?.folder_id])

  useEffect(() => {
    adminApi
      .get<ChunkSettings>('/admin/settings/chunk')
      .then((r) => {
        setDefaultStrategy(r.data.default_strategy || 'fixed')
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!loggedIn) return
    adminApi
      .get<{ items: FolderDimension[] }>('/admin/knowledge/folder-dimensions', { params: scopeParams })
      .then((r) => setDimensions(r.data.items || []))
      .catch(() => setDimensions([]))
  }, [loggedIn, scopeParams])

  useEffect(() => {
    if (!loggedIn || !dimensions.length) {
      setFolderMetaById(new Map())
      return
    }
    if (!selectedFolderIdsKey) {
      setFolderMetaById(new Map())
      return
    }
    const folderIds = selectedFolderIdsKey
      .split(',')
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0)
    const folderIdSet = new Set(folderIds)
    let cancelled = false
    void (async () => {
      const next = new Map<number, FolderLabel>()
      for (const dim of dimensions) {
        try {
          const r = await adminApi.get<{ items: FolderLabel[] }>('/admin/knowledge/folders/labels', {
            params: { ...scopeParams, dimension_id: dim.id },
          })
          if (cancelled) return
          for (const f of r.data.items || []) {
            if (folderIdSet.has(f.id)) next.set(f.id, f)
          }
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setFolderMetaById(next)
    })()
    return () => {
      cancelled = true
    }
  }, [loggedIn, dimensions, scopeParams, selectedFolderIdsKey])

  const isView = pageMode === 'view'
  const isCreate = pageMode === 'create'
  const isEditing = pageMode === 'edit'
  const editingItemId = savedItem?.id ?? (viewItemIdValid && !isCreate ? viewItemId : null)
  const isDraftDoc =
    (savedItem?.doc_status || docStatusWatch || 'normal') === 'draft'

  useEffect(() => {
    setPageMode(parsePageMode(viewItemIdValid, searchParams.get('mode')))
  }, [viewItemIdValid, searchParams])

  useEffect(() => {
    if (!isCreate) return
    const initialDimensionIds =
      Number.isFinite(initialDimensionId) && initialDimensionId > 0
        ? [initialDimensionId]
        : defaultDimensionId(dimensions) != null
          ? [defaultDimensionId(dimensions)!]
          : []
    form.setFieldsValue({
      folder_ids: folderValid ? [initialFolderId] : [],
      dimension_ids: initialDimensionIds,
      doc_status: 'normal',
      category: '文档',
      tags: [],
      summary: '',
    })
  }, [form, folderValid, initialFolderId, initialDimensionId, dimensions, isCreate])

  useEffect(() => {
    if (!isCreate) return
    setDocType(parseDocType(searchParams.get('type')))
  }, [searchParams, isCreate])

  useEffect(() => {
    if (!loggedIn || !viewItemIdValid || savedItem) return
    setDocLoading(true)
    adminApi
      .get<SavedDocItem & { files_json?: FileRec[] | null }>(
        `/admin/knowledge/items/${viewItemId}`,
        { params: scopeParams },
      )
      .then(async (r) => {
        const item = r.data
        const body = await loadDocBody(item.id, item.files_json, scopeParams)
        setSavedItem(itemToSaved(item))
        setTitle(item.title)
        setDocType(body.docType)
        if (body.docType === 'markdown') {
          setMarkdown(body.content)
        } else {
          setRichtext(body.content)
        }
        setEditorBodyEmpty(
          isDocumentBodyEmpty(body.content, body.docType === 'markdown' ? 'markdown' : 'html'),
        )
        form.setFieldsValue({
          folder_ids: item.folder_ids?.length
            ? item.folder_ids
            : item.folder_id
              ? [item.folder_id]
              : [],
          dimension_ids: item.dimension_ids?.length
            ? item.dimension_ids
            : item.folder_dimension_id
              ? [item.folder_dimension_id]
              : [],
          doc_status: item.doc_status || 'normal',
          category: item.category || '文档',
          tags: tagsToArray(item.tags),
          summary: item.summary || '',
        })
      })
      .catch((e) => message.error(formatApiError(e, '加载文档失败')))
      .finally(() => setDocLoading(false))
  }, [loggedIn, viewItemIdValid, viewItemId, scopeParams, savedItem, form])

  const changeDocType = useCallback(
    (next: DocType) => {
      if (next === docType) return
      setDocType(next)
      const content = next === 'markdown' ? markdown : richtext
      setEditorBodyEmpty(
        isDocumentBodyEmpty(content, next === 'markdown' ? 'markdown' : 'html'),
      )
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('type', next)
      setSearchParams(nextParams, { replace: true })
    },
    [docType, searchParams, setSearchParams, markdown, richtext],
  )

  const enterViewMode = useCallback(
    (item: SavedDocItem & { folder_id?: number | null }) => {
      const saved = itemToSaved(item)
      setSavedItem(saved)
      setPageMode('view')
      void navigate(docPath(scope, saved.folder_id, saved.id), { replace: true })
    },
    [navigate, scope],
  )

  const startEdit = useCallback(() => {
    setPageMode('edit')
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('mode', 'edit')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const submit = async (forceStatus?: 'normal' | 'draft') => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      message.warning('请输入标题')
      setActiveTab('body')
      return
    }
    const content = (
      docType === 'markdown'
        ? (mdEditorRef.current?.getDocumentContent() ?? markdown)
        : richtext
    ).trim()
    if (!content) {
      message.warning('请填写文档正文')
      setActiveTab('body')
      return
    }

    const folderIds = (form.getFieldValue('folder_ids') as number[] | undefined) || []
    const dimensionIds = (form.getFieldValue('dimension_ids') as number[] | undefined) || []
    const targetFolderIds =
      folderIds.length > 0
        ? folderIds
        : folderValid
          ? [initialFolderId]
          : []
    if (!targetFolderIds.length) {
      message.warning('请选择所属文件夹')
      setActiveTab('attrs')
      return
    }

    let values: {
      summary?: string
      doc_status?: 'normal' | 'draft'
      tags?: string[]
      category?: string
    }
    try {
      values = await form.validateFields(['doc_status', 'tags', 'summary', 'category'])
    } catch {
      setActiveTab('attrs')
      return
    }
    const docStatus = forceStatus ?? values.doc_status ?? 'normal'
    const category = (values.category || form.getFieldValue('category') || '文档').trim()
    if (!category) {
      message.warning('请选择文档类型')
      setActiveTab('attrs')
      return
    }

    setSubmittingAs(docStatus)
    const payload = {
      title: trimmedTitle,
      folder_id: targetFolderIds[0],
      folder_ids: targetFolderIds,
      dimension_ids: dimensionIds,
      content,
      category,
      tags: tagsFromForm(values.tags),
      summary: (values.summary || '').trim(),
      doc_status: docStatus,
      chunk_strategy: defaultStrategy,
    }

    const req = editingItemId && !isCreate
      ? adminApi.post(
          '/admin/knowledge/items/update-document',
          { ...payload, item_id: editingItemId },
          { params: scopeParams },
        )
      : adminApi.post('/admin/knowledge/items/create-document', {
          ...payload,
          doc_type: docType,
        }, { params: scopeParams })

    req
      .then((r) => {
        const isUpdate = Boolean(editingItemId && !isCreate)
        message.success(
          isUpdate
            ? docStatus === 'draft'
              ? '草稿已保存'
              : '文档已保存，正在后台重新索引'
            : docStatus === 'draft'
              ? '草稿已保存'
              : '文档已创建，正在后台索引',
        )
        publishKnowledgeDocSaved({
          scope,
          folderId: targetFolderIds[0],
          itemId: r.data.id,
          indexJobId: r.data.index_job_id ?? undefined,
        })
        enterViewMode(r.data)
      })
      .catch((e) => message.error(formatApiError(e, editingItemId ? '保存失败' : '创建失败')))
      .finally(() => setSubmittingAs(null))
  }

  const viewDimensionNames = useMemo(() => {
    const fromPlacements = savedItem?.dimension_placements?.map((d) => d.dimension_name).filter(Boolean)
    if (fromPlacements?.length) return fromPlacements
    const ids = savedItem?.dimension_ids?.length
      ? savedItem.dimension_ids
      : savedItem?.folder_dimension_id
        ? [savedItem.folder_dimension_id]
        : selectedDimensionIds
    return ids
      .map((id) => {
        const dim = dimensions.find((d) => d.id === id)
        return dim ? dimensionDisplayName(dim) : null
      })
      .filter(Boolean) as string[]
  }, [dimensions, savedItem, selectedDimensionIds])

  const viewFolderLabels = useMemo(() => {
    const placements = savedItem?.folder_placements || []
    if (placements.length) {
      return placements.map((p) => {
        const prefix = p.dimension_name ? `${p.dimension_name} / ` : ''
        return `${prefix}${p.folder_path || p.folder_id}`
      })
    }
    const ids = savedItem?.folder_ids?.length
      ? savedItem.folder_ids
      : savedItem?.folder_id
        ? [savedItem.folder_id]
        : folderIdsWatch ?? []
    return ids.map((id) => {
      const meta = folderMetaById.get(id)
      return meta?.name || `#${id}`
    })
  }, [folderMetaById, savedItem, folderIdsWatch])

  const viewTags = useMemo(() => tagsToArray(savedItem?.tags), [savedItem?.tags])

  const getDocumentContent = useCallback(() => {
    if (docType === 'markdown') {
      return mdEditorRef.current?.getDocumentContent() ?? markdown
    }
    return richtext
  }, [docType, markdown, richtext])

  const getEditorSelection = useCallback(() => {
    if (docType === 'markdown') return mdEditorRef.current?.getSelectionText() || ''
    return htmlEditorRef.current?.getSelectionText() || ''
  }, [docType])

  const handleAiInsert = useCallback(
    (
      content: string,
      mode: 'cursor' | 'append' | 'replace' | 'afterSelection',
      opts?: { pinnedSelection?: string },
    ) => {
      const pin = opts?.pinnedSelection?.trim()
      if (pin && (mode === 'replace' || mode === 'afterSelection')) {
        const ok =
          docType === 'markdown'
            ? mdEditorRef.current?.selectText(pin)
            : htmlEditorRef.current?.selectText(pin)
        if (!ok) {
          message.warning('未在正文找到原选区，请手动选中后再插入')
          return
        }
      }
      if (docType === 'markdown') {
        mdEditorRef.current?.insertContent(content, mode)
      } else {
        htmlEditorRef.current?.insertContent(content, mode)
      }
      if (mode === 'replace' || mode === 'afterSelection') {
        writingPinnedSelectionRef.current = ''
      }
    },
    [docType],
  )

  const getContextBeforeCursor = useCallback(() => {
    if (docType === 'markdown') {
      return mdEditorRef.current?.getContentBeforeCursor() ?? ''
    }
    return htmlEditorRef.current?.getContentBeforeCursor() ?? ''
  }, [docType])

  const getContextAfterCursor = useCallback(() => {
    if (docType === 'markdown') {
      return mdEditorRef.current?.getContentAfterCursor() ?? ''
    }
    return htmlEditorRef.current?.getContentAfterCursor() ?? ''
  }, [docType])

  const handleRestoreCursorAnchor = useCallback(() => {
    if (docType === 'markdown') {
      mdEditorRef.current?.restoreCursorAnchor()
    } else {
      htmlEditorRef.current?.restoreCursorAnchor()
    }
  }, [docType])

  const getInlinePanelAnchorRect = useCallback(() => {
    if (docType === 'markdown') {
      return mdEditorRef.current?.getSavedAnchorRect() ?? null
    }
    return htmlEditorRef.current?.getSavedAnchorRect() ?? null
  }, [docType])

  const handleSlashAiTrigger = useCallback(() => {
    const saved =
      docType === 'markdown'
        ? mdEditorRef.current?.saveCursorAnchor()
        : htmlEditorRef.current?.saveCursorAnchor()
    if (!saved) return
    inlineSelectionRef.current = { selection: '', selectionPlain: '' }
    setInlinePanelSelection({ selection: '', selectionPlain: '' })
    setInlinePanelMode('cursor')
    setInlinePanelOpen(true)
  }, [docType])

  const handleInlineInsert = useCallback(
    (content: string, mode: DocInlineInsertMode) => {
      if (mode === 'cursor') {
        handleRestoreCursorAnchor()
        handleAiInsert(content, 'cursor')
        return
      }
      const pin =
        inlineSelectionRef.current.selectionPlain.trim() ||
        inlineSelectionRef.current.selection.trim()
      handleAiInsert(content, mode, pin ? { pinnedSelection: pin } : undefined)
    },
    [handleAiInsert, handleRestoreCursorAnchor],
  )

  const handleDescribeIntent = useCallback(
    (selection: string, selectionPlain?: string) => {
      const plain = (selectionPlain || selection).trim()
      inlineSelectionRef.current = { selection, selectionPlain: plain }
      setInlinePanelSelection({ selection, selectionPlain: plain })
      if (docType === 'markdown') {
        mdEditorRef.current?.selectText(plain)
        mdEditorRef.current?.saveCursorAnchor()
      } else {
        htmlEditorRef.current?.selectText(plain)
        htmlEditorRef.current?.saveCursorAnchor()
      }
      setInlinePanelMode('selection')
      setInlinePanelOpen(true)
    },
    [docType],
  )

  const handlePinWritingSelection = useCallback((text: string) => {
    writingPinnedSelectionRef.current = text.trim()
  }, [])

  const handleLocateWritingSelection = useCallback(() => {
    const text = writingPinnedSelectionRef.current
    if (!text) return
    const ok =
      docType === 'markdown'
        ? mdEditorRef.current?.selectText(text)
        : htmlEditorRef.current?.selectText(text)
    if (!ok) message.warning('未在正文找到对应段落，可能内容已变更')
  }, [docType])

  const handleSelectionAiAction = useCallback(
    (action: 'polish' | 'expand' | 'continue', selection: string, selectionPlain?: string) => {
      writingAssistantRef.current?.runQuickAction(action, { selection, selectionPlain })
    },
    [],
  )

  const showDocTemplatePicker = !isView && editorBodyEmpty
  const [templatePickerMounted, setTemplatePickerMounted] = useState(showDocTemplatePicker)
  const [templatePickerVisible, setTemplatePickerVisible] = useState(false)

  useEffect(() => {
    if (showDocTemplatePicker) {
      setTemplatePickerMounted(true)
      let frame2 = 0
      const frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => setTemplatePickerVisible(true))
      })
      return () => {
        cancelAnimationFrame(frame1)
        cancelAnimationFrame(frame2)
      }
    }
    setTemplatePickerVisible(false)
    const timer = window.setTimeout(() => setTemplatePickerMounted(false), 320)
    return () => window.clearTimeout(timer)
  }, [showDocTemplatePicker])

  const applyDocTemplate = useCallback(
    (content: string) => {
      const trimmed = (content || '').trim()
      if (!trimmed) {
        message.warning('模板内容为空，请先在后台「文档模板」中维护正文')
        return
      }
      if (docType === 'markdown') {
        setMarkdown(trimmed)
        mdEditorRef.current?.setDocumentContent(trimmed)
      } else {
        setRichtext(trimmed)
        htmlEditorRef.current?.setDocumentContent(trimmed)
      }
      message.success('已应用模板')
    },
    [docType],
  )

  const bodyEditor = (
    <div className="kb-create-doc-body-layout">
      <div className="kb-create-doc-body-pane">
        <div className="kb-create-doc-editor-stack">
          <div className="kb-create-doc-editor-main" ref={editorMainRef}>
            {isView ? (
              docType === 'markdown' ? (
                <MarkdownDocViewer value={markdown} />
              ) : (
                <div
                  className="kb-create-doc-html-view"
                  dangerouslySetInnerHTML={{ __html: richtext }}
                />
              )
            ) : docType === 'markdown' ? (
              <MarkdownDocEditor
                key={`md-${editingItemId ?? 'new'}`}
                ref={mdEditorRef}
                value={markdown}
                onChange={setMarkdown}
                onBodyEmptyChange={setEditorBodyEmpty}
              />
            ) : (
              <HtmlDocEditor
                key={`html-${editingItemId ?? 'new'}`}
                ref={htmlEditorRef}
                value={richtext}
                onChange={setRichtext}
                onBodyEmptyChange={setEditorBodyEmpty}
              />
            )}
            {templatePickerMounted ? (
              <div
                className={[
                  'kb-create-doc-template-below',
                  templatePickerVisible ? 'kb-create-doc-template-below--visible' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <DocTemplatePicker
                  format={docType === 'markdown' ? 'markdown' : 'html'}
                  onSelect={applyDocTemplate}
                />
              </div>
            ) : null}
            {!isView ? (
              <DocSelectionAiToolbar
                containerRef={editorMainRef}
                getSelectionText={getEditorSelection}
                onAction={handleSelectionAiAction}
                onDescribeIntent={handleDescribeIntent}
              />
            ) : null}
            {!isView ? (
              <DocEditorAtAiTrigger containerRef={editorMainRef} onTrigger={handleSlashAiTrigger} />
            ) : null}
            {!isView ? (
              <DocInlineWritingPanel
                open={inlinePanelOpen}
                mode={inlinePanelMode}
                onClose={() => setInlinePanelOpen(false)}
                format={docType === 'markdown' ? 'markdown' : 'html'}
                title={title}
                category={categoryWatch || ''}
                getAnchorRect={getInlinePanelAnchorRect}
                scrollRootRef={editorMainRef}
                getContextBeforeCursor={getContextBeforeCursor}
                getContextAfterCursor={getContextAfterCursor}
                getDocumentContent={getDocumentContent}
                selection={inlinePanelSelection.selection}
                selectionPlain={inlinePanelSelection.selectionPlain}
                onInsert={handleInlineInsert}
              />
            ) : null}
          </div>
        </div>
      </div>
      <DocWritingAssistant
        ref={isView ? undefined : writingAssistantRef}
        variant={isView ? 'reading' : 'writing'}
        format={docType === 'markdown' ? 'markdown' : 'html'}
        title={title}
        category={categoryWatch || ''}
        getDocumentContent={getDocumentContent}
        getSelection={isView ? () => '' : getEditorSelection}
        onInsert={isView ? () => {} : handleAiInsert}
        onPinSelection={isView ? undefined : handlePinWritingSelection}
        onLocateSelection={isView ? undefined : handleLocateWritingSelection}
        hideSelectionContext={isView || inlinePanelOpen}
      />
    </div>
  )

  const attrsForm = isView ? (
    <div className="kb-create-doc-attrs-pane">
      <div className="kb-attrs-sheet">
        <div className="kb-attrs-row kb-attrs-row--2">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">创建人</div>
            <div className="kb-attrs-value kb-attrs-value--text">
              {savedItem?.created_by_name || creatorName}
            </div>
          </div>
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">
              {savedItem?.created_at ? '创建时间' : '知识库'}
            </div>
            <div className="kb-attrs-value kb-attrs-value--text">
              {savedItem?.created_at
                ? formatDate(savedItem.created_at)
                : KNOWLEDGE_SCOPE_META[scope].title}
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--2">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">虚拟分类</div>
            <div
              className="kb-attrs-value kb-attrs-value--text kb-attrs-value--multiline"
              title={viewDimensionNames.join('、') || undefined}
            >
              {viewDimensionNames.length ? viewDimensionNames.join('、') : '—'}
            </div>
          </div>
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">文档状态</div>
            <div className="kb-attrs-value kb-attrs-value--text">
              {isDraftDoc ? '草稿' : '正常'}
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--1">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">所属文件夹</div>
            <div
              className="kb-attrs-value kb-attrs-value--text kb-attrs-value--multiline"
              title={viewFolderLabels.join('；') || undefined}
            >
              {viewFolderLabels.length ? viewFolderLabels.join('；') : '—'}
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--2">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">文档类型</div>
            <div className="kb-attrs-value kb-attrs-value--text">{savedItem?.category || '—'}</div>
          </div>
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">标签</div>
            <div className="kb-attrs-value kb-attrs-value--text">
              {viewTags.length ? viewTags.join('、') : '—'}
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--1 kb-attrs-row--summary">
          <div className="kb-attrs-cell kb-attrs-cell--top">
            <div className="kb-attrs-label">摘要</div>
            <div className="kb-attrs-value kb-attrs-value--text kb-attrs-value--multiline">
              {savedItem?.summary || '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="kb-create-doc-attrs-pane">
      <div className="kb-attrs-sheet">
        <div className="kb-attrs-row kb-attrs-row--2">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">创建人</div>
            <div className="kb-attrs-value kb-attrs-value--text">
              {savedItem?.created_by_name || creatorName}
            </div>
          </div>
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">
              {savedItem?.created_at ? '创建时间' : '知识库'}
            </div>
            <div className="kb-attrs-value kb-attrs-value--text">
              {savedItem?.created_at
                ? formatDate(savedItem.created_at)
                : KNOWLEDGE_SCOPE_META[scope].title}
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--2">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">虚拟分类</div>
            <div className="kb-attrs-value">
              <Form.Item name="dimension_ids" noStyle>
                <KnowledgeDimensionMultiBrowseField dimensions={dimensions} trigger="click" />
              </Form.Item>
            </div>
          </div>
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">文档状态</div>
            <div className="kb-attrs-value">
              <Form.Item name="doc_status" noStyle>
                <Select
                  variant="borderless"
                  popupMatchSelectWidth
                  options={DOC_STATUS_OPTIONS}
                  className="kb-attrs-control"
                />
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--1">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">所属文件夹</div>
            <div className="kb-attrs-value">
              <Form.Item
                name="folder_ids"
                rules={[
                  {
                    validator: (_, value) =>
                      Array.isArray(value) && value.length > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error('请选择文件夹')),
                  },
                ]}
                noStyle
              >
                <KnowledgeFolderMultiBrowseField
                  scope={scope}
                  dimensions={dimensions}
                  dimensionIds={selectedDimensionIds.length ? selectedDimensionIds : undefined}
                  placements={savedItem?.folder_placements}
                  placeholder="请选择所属文件夹"
                  trigger="click"
                  className="kb-attrs-browse--folder"
                />
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--2">
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">文档类型</div>
            <div className="kb-attrs-value">
              <Form.Item
                name="category"
                rules={[{ required: true, message: '请选择文档类型' }]}
                noStyle
              >
                <KnowledgeCategoryBrowseField scope={scope} trigger="click" />
              </Form.Item>
            </div>
          </div>
          <div className="kb-attrs-cell">
            <div className="kb-attrs-label">标签</div>
            <div className="kb-attrs-value">
              <Form.Item name="tags" noStyle>
                <KnowledgeTagsBrowseField
                  scope={scope}
                  trigger="click"
                  placeholder={
                    selectedFolder?.ai_auto_tags ? '请选择标签（可留空由 AI 生成）' : '请选择标签'
                  }
                />
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="kb-attrs-row kb-attrs-row--1 kb-attrs-row--summary">
          <div className="kb-attrs-cell kb-attrs-cell--top">
            <div className="kb-attrs-label">摘要</div>
            <div className="kb-attrs-value">
              <Form.Item name="summary" noStyle>
                <Input.TextArea
                  variant="borderless"
                  rows={4}
                  placeholder={
                    selectedFolder?.ai_auto_summary
                      ? '请输入摘要；留空且目录已开启 AI 时，索引完成后自动生成'
                      : '请输入摘要'
                  }
                  maxLength={2000}
                  className="kb-attrs-textarea"
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (!ready || (viewItemIdValid && docLoading && !savedItem)) {
    return (
      <StandaloneShell centered>
        <PageLoading />
      </StandaloneShell>
    )
  }

  if (!loggedIn) {
    const redirect = `${location.pathname}${location.search}`
    return (
      <StandaloneShell centered>
        <Alert
          type="info"
          showIcon
          message="请先登录"
          description="新建文档需使用管理账号登录。"
          action={
            <Button
              type="primary"
              onClick={() => void navigate(`/manage/login?redirect=${encodeURIComponent(redirect)}`)}
            >
              去登录
            </Button>
          }
        />
      </StandaloneShell>
    )
  }

  if (!folderValid && !viewItemIdValid) {
    return (
      <StandaloneShell centered>
        <Alert
          type="warning"
          showIcon
          message="无法新建文档"
          description="未指定有效文件夹。请先在知识库左侧选中文件夹，再通过「新建」打开本页。"
          action={
            <Button type="primary" onClick={() => void navigate(listPath(scope))}>
              返回知识库
            </Button>
          }
        />
      </StandaloneShell>
    )
  }

  return (
    <StandaloneShell>
      <Form form={form} component={false}>
        <div className="kb-create-doc-root">
        <header className="kb-create-doc-header">
          <div className="kb-create-doc-header-left">
            {isView ? (
              <>
                <Typography.Title level={4} className="kb-create-doc-view-title" title={title}>
                  {title || '未命名文档'}
                </Typography.Title>
                <Typography.Text type="secondary" className="kb-create-doc-view-format">
                  {DOC_TYPE_OPTIONS.find((o) => o.value === docType)?.label}
                </Typography.Text>
              </>
            ) : (
              <>
                <Typography.Text className="kb-create-doc-header-label">标题</Typography.Text>
                <Input
                  className="kb-create-doc-header-title"
                  placeholder="请输入文档标题"
                  value={title}
                  maxLength={500}
                  onChange={(e) => setTitle(e.target.value)}
                />
                {isEditing ? (
                  <Typography.Text type="secondary" className="kb-create-doc-view-format">
                    {DOC_TYPE_OPTIONS.find((o) => o.value === docType)?.label}
                  </Typography.Text>
                ) : (
                  <Segmented
                    className="kb-create-doc-type-switch"
                    value={docType}
                    options={DOC_TYPE_OPTIONS}
                    onChange={(v) => changeDocType(v as DocType)}
                  />
                )}
              </>
            )}
          </div>
          <Space className="kb-create-doc-header-actions" size={8}>
            {isCreate ? (
              <>
                <Button
                  type="primary"
                  loading={submittingAs === 'normal'}
                  disabled={submittingAs !== null}
                  onClick={() => void submit('normal')}
                >
                  提交
                </Button>
                <Button
                  loading={submittingAs === 'draft'}
                  disabled={submittingAs !== null}
                  onClick={() => {
                    form.setFieldValue('doc_status', 'draft')
                    void submit('draft')
                  }}
                >
                  存为草稿
                </Button>
              </>
            ) : null}
            {isView ? (
              <>
                <Button onClick={startEdit}>编辑</Button>
                {isDraftDoc ? (
                  <Button
                    type="primary"
                    loading={submittingAs === 'normal'}
                    disabled={submittingAs !== null}
                    onClick={() => void submit('normal')}
                  >
                    提交
                  </Button>
                ) : null}
              </>
            ) : null}
            {isEditing ? (
              <>
                <Button
                  type="primary"
                  loading={submittingAs === 'normal'}
                  disabled={submittingAs !== null}
                  onClick={() => void submit('normal')}
                >
                  {isDraftDoc ? '提交' : '保存'}
                </Button>
                <Button
                  loading={submittingAs === 'draft'}
                  disabled={submittingAs !== null}
                  onClick={() => {
                    form.setFieldValue('doc_status', 'draft')
                    void submit('draft')
                  }}
                >
                  存为草稿
                </Button>
              </>
            ) : null}
          </Space>
        </header>

        <Tabs
          className="kb-create-doc-main-tabs"
          destroyOnHidden={false}
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'body', label: '正文', children: bodyEditor },
            { key: 'attrs', label: '属性', children: attrsForm, forceRender: true },
            ...(scope === 'enterprise'
              ? [
                  {
                    key: 'share',
                    label: '共享',
                    children: (
                      <KnowledgeItemShareTab
                        itemId={editingItemId}
                        folderId={shareFolderId}
                        scope={scope}
                        editable={!isView}
                        active={activeTab === 'share'}
                      />
                    ),
                    forceRender: true,
                  },
                ]
              : []),
            {
              key: 'fields',
              label: '字段抽取',
              children: editingItemId ? (
                <KnowledgeItemKeyValuesPreview
                  itemId={editingItemId}
                  scopeParams={scopeParams}
                  active={activeTab === 'fields'}
                />
              ) : (
                <Typography.Text type="secondary">保存文档后可查看字段抽取结果</Typography.Text>
              ),
            },
          ]}
        />
        </div>
      </Form>
    </StandaloneShell>
  )
}
